import { EntityTable } from '~/store/slices/dipsSlice'
import { useStore } from '~/store/store'
import { UnifiedScheduledSurgery } from '~/store/transformers/scheduledSurgeryTransformer'
import { UnifiedUnScheduledSurgery } from '~/store/transformers/unScheduledSurgery/unScheduledSurgeryTransformer'
import { selectSurgeryFolderMetadata, type SurgeryFolderMetadatum } from '~/storm'
import { getBookingIdOrId, isSurgeryLetterSent } from '~/utils/dips'

import { SurgeryMetadataCountedData } from './dataTypes'

type SurgeryMetadataLookup = Record<string, SurgeryFolderMetadatum>

function getSurgeryMetadataLookups(): { byFolderId: SurgeryMetadataLookup; byBookingId: SurgeryMetadataLookup } {
    const entries = selectSurgeryFolderMetadata(useStore.getState())
    return {
        byFolderId: Object.fromEntries(entries.map(metadata => [metadata.folder_id, metadata])),
        byBookingId: Object.fromEntries(entries.map(metadata => [metadata.latest_booking_id, metadata])),
    }
}

type SurgeryMetadataCounts = Omit<SurgeryMetadataCountedData, 'departmentName' | 'surgeryType' | 'orgData'>

type Counters = {
    [K in keyof Omit<SurgeryMetadataCounts, 'totalItems'>]: number
}

export function getSurgeryMetadataCounts(surgeries: EntityTable<string, UnifiedScheduledSurgery | UnifiedUnScheduledSurgery>): SurgeryMetadataCounts {
    const totalItems = surgeries.allIds.length

    const counters: Counters = {
        metadataByFolderIdCount: 0,
        missingFolderIdCount: 0,
        alwaysAvailableItems: 0,
        availableFromItems: 0,
        unavailableBetweenItems: 0,
        waitingItems: 0,

        newItems: 0,
        clearedItems: 0,
        nonClearedItems: 0,

        confirmedItems: 0,
        prioItems: 0,
        shortNoticeItems: 0,

        nonExistingMetadataItems: 0,

        letterNotSentItems: 0,
    }

    const { byFolderId: surgeryMetadataByFolderId, byBookingId: surgeryMetadataByBookingId } = getSurgeryMetadataLookups()

    for (let i = 0; i < totalItems; i++) {
        const surgeryId = surgeries.allIds[i]
        if (!surgeryId) continue

        const surgery = surgeries.byId[surgeryId]
        if (!surgery) continue

        const folderId = surgery.surgeryOrderDetails?.folderId
        if (!folderId) {
            counters.missingFolderIdCount++
        }
        const metadata = folderId ? surgeryMetadataByFolderId[folderId] : surgeryMetadataByBookingId[getBookingIdOrId(surgery)]

        if (!metadata) {
            counters.nonExistingMetadataItems++
            // Count default values for non-existing metadata
            counters.alwaysAvailableItems++
            counters.newItems++
            continue
        }

        const patientAvailability = metadata.availability_periods?.at(0)

        if (patientAvailability?.type === 'available_from') counters.availableFromItems++
        else if (patientAvailability?.type === 'unavailable_between') counters.unavailableBetweenItems++
        else if (patientAvailability?.type === 'waiting') counters.waitingItems++
        else counters.alwaysAvailableItems++

        if (metadata.patient_ready) counters.clearedItems++
        else if (metadata.patient_ready === false) counters.nonClearedItems++
        else counters.newItems++

        if (metadata.patient_confirmed) counters.confirmedItems++
        if (metadata.patient_prioritized) counters.prioItems++
        if (metadata.patient_short_notice) counters.shortNoticeItems++

        if (!isSurgeryLetterSent(surgery)) counters.letterNotSentItems++
    }

    const countsWithPercentages = Object.fromEntries(
        Object.entries(counters).map(([key, value]) => {
            const percentage = ((value / totalItems) * 100).toFixed(1)
            return [key, `${value} (${percentage}%)`]
        })
    ) as { [K in keyof Counters]: string }

    return {
        totalItems,
        ...countsWithPercentages,
        metadataByFolderIdCount: Object.keys(surgeryMetadataByFolderId).length,
    }
}
